import React, { useEffect, useState } from 'react';
import { convertTimestampToDateTime } from '../../helper/helper';
import { OrderDetailsModel, OrderTypeEnum } from '../../interfaces/order.interface';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { formatOrderCurrency } from '../../utils/currencyFormat';

interface PurchaseConfirmationProps {
  onClose: () => void;
  order: OrderDetailsModel | null;
}

const PurchaseConfirmation = ({ onClose, order }: PurchaseConfirmationProps) => {
  const user = useSelector((state: RootState) => state.user.user);
  const [bookPrice, setBookPrice] = useState<number>(12.95);
  const [subTotal, setSubTotal] = useState<number | string>(0);
  const [description, setDescription] = useState({
    title: '',
    titleSummary: '',
    processDate: '',
    summaryDetail: {
      row1_title: '',
      row2_title: '',
      row3_title: ''
    },
    info1: ''
  });

  useEffect(() => {
    switch (order?.orderType) {
      case OrderTypeEnum.BOOK_PURCHASE:
        setDescription((prevDescription) => ({
          ...prevDescription, // Copy the previous state
          title: 'Purchase Confirmation',
          titleSummary: 'Order Summary',
          processDate: convertTimestampToDateTime(order?.timestamp, true),
          info1: `${user?.firstName}, thank you for your order!`
        }));
        break;
      case OrderTypeEnum.CREDITS_PURCHASE:
        setDescription((prevDescription) => ({
          ...prevDescription, // Copy the previous state
          title: 'Image Credits Purchase Confirmation',
          titleSummary: 'Image credit Purchase Summary',
          processDate: convertTimestampToDateTime(order?.timestamp, true),
          info1: `${user?.firstName}, thank you for purchasing Image credits.`
        }));
        break;
      case OrderTypeEnum.SUBSCRIPTION_PURCHASE:
        setDescription((prevDescription) => ({
          ...prevDescription, // Copy the previous state
          title: 'Purchase Confirmation',
          titleSummary: 'Plan Subscription Summary',
          processDate: convertTimestampToDateTime(order?.timestamp, true),
          info1: `${user?.firstName}, thank you for subscribing to plan <strong>${order?.extraInfo?.plan}</strong>.`
        }));
        break;
      default:
        break;
    }

    setSubTotal(
      (
        +(bookPrice) * +(order?.quantity ?? 0).toFixed(2) +
        +(order?.additionalPageCost || 0) -
        +(order?.couponDiscount || 0)
      ).toFixed(2)
    );
  }, [order]);

  const bookPurchasePriceAfterDiscount = (order: OrderDetailsModel): number => {
    let discountedPrice: number = +(
      +(bookPrice) * +(order?.quantity ?? 0).toFixed(2) +
      +(order?.additionalPageCost || 0) -
      +(order?.couponDiscount || 0)
    ).toFixed(2);
    if (discountedPrice < 1) {
      discountedPrice = 0;
    }
    return discountedPrice;
  };

  const calculateBookPurchaseTotalAmount = (order: OrderDetailsModel): string => {
    let total = '0';
    total = (
      +(order?.price) -
      +(order?.couponDiscount || 0) +
      +(order?.shippingAmount ?? 0)
    ).toFixed(2);

    // const subTotal = parseFloat(order?.price || '0') + parseFloat(order?.additionalPageCost?.toString() || '0');

    if (+total <= 0) {
      total = '0';
    }
    return total;
  };

  const checkCoupon = (order: OrderDetailsModel) => {
    if (order?.coupon?.startsWith('GC')) {
      return 'Gift Card';
    }
    return 'discount';
  };

  return (
    <div>
      <button
        className="w-20 flex justify-center bg-storyblueSecondLightText text-white font-bold rounded-2xl text-base px-4 py-2 mb-4 float-right"
        onClick={onClose}
      >
        Back
      </button>
      <div
        className={`w-[18rem] md:w-full text-storyblue text-lg text-left flex ${
          order?.orderType === OrderTypeEnum.BOOK_PURCHASE
            ? 'items-start md:[align-items:normal]'
            : 'items-center'
        }  flex-col gap-8`}
      >
        <h1 className="text-xl md:text-2xl font-bold">{description?.title}</h1>
        <p>
          <div dangerouslySetInnerHTML={{ __html: description?.info1 }} />
        </p>

        {order?.orderType === OrderTypeEnum.BOOK_PURCHASE && (
          <p>
            We’ve received your order and will contact you as soon as your package is shipped. You can find
            your purchase information below.
          </p>
        )}

        {order?.orderType === OrderTypeEnum.CREDITS_PURCHASE && (
          <p>
            You have purchased <strong>{order?.quantity}</strong> Image Credits for{' '}
            <strong>{formatOrderCurrency(order?.price || 0, order?.currency, order?.currencySymbol)}</strong>
          </p>
        )}

        {order?.orderType === OrderTypeEnum.SUBSCRIPTION_PURCHASE && (
          <div>
            <p>
              <strong>Image Credits:</strong> {order?.extraInfo?.credits}
            </p>
            <p>
              <strong>Additional Image Credits:</strong> {order?.extraInfo?.additionImageCreditsInfo}
            </p>
            <p>
              <strong>Unlimited Downloads:</strong> {order?.extraInfo?.unlimitedDownloads}
            </p>
          </div>
        )}

        <div className="text-center">
          <p className="text-[16px] font-bold">{description?.titleSummary}</p>
          <p>{description?.processDate}</p>
        </div>
        <div className="flex justify-center items-center flex-wrap md:flex-nowrap gap-4 md:gap-16">
          <img src="/bookcover.png" alt="pic" className="w-40 h-40" />
          {order?.orderType === OrderTypeEnum.SUBSCRIPTION_PURCHASE && (
            <div className="font-bold text-sm flex flex-col gap-2">
              <div className="flex">
                <div className="flex flex-col text-left gap-2 w-44">
                  <p>Plan Cost:</p>

                  {/* <p>discount:</p> */}
                  <p>{checkCoupon(order)}:</p>
                </div>
                <div className="flex flex-col text-left gap-2 w-44">
                  <p>{formatOrderCurrency(order?.price || 0, order?.currency, order?.currencySymbol)}</p>
                  {/* <p>$ {order?.couponDiscount}</p> */}
                  <p>
                    {(() => {
                      const price = parseFloat(order?.price || '0');
                      const additionalPageCost = +(order?.additionalPageCost || '0');
                      const totalCost = price + additionalPageCost;

                      return totalCost <= 0 
                        ? formatOrderCurrency(0, order?.currency, order?.currencySymbol)
                        : formatOrderCurrency(order?.couponDiscount || 0, order?.currency, order?.currencySymbol);
                    })()}
                  </p>
                </div>
              </div>
              <hr className="border-1 w-56" style={{ borderColor: 'gray' }} />
              <div className="flex  text-left">
                <p className="w-44">Total Price:</p>
                {/* <p>$ {parseFloat(order?.price) - parseFloat(order?.couponDiscount || 0)}</p> */}
                <p>{formatOrderCurrency(calculateBookPurchaseTotalAmount(order), order?.currency, order?.currencySymbol)}</p>
              </div>
            </div>
          )}

          {order?.orderType === OrderTypeEnum.CREDITS_PURCHASE && (
            <div className="font-bold text-sm flex flex-col gap-2">
              <div className="flex">
                <div className="flex flex-col text-left gap-2 w-44">
                  <p>Image Credit Price:</p>
                  <p>Discount:</p>
                </div>
                <div className="flex flex-col text-left gap-2 w-44">
                  <p>{formatOrderCurrency(order?.price || 0, order?.currency, order?.currencySymbol)}</p>
                  <p>{formatOrderCurrency(order?.couponDiscount || 0, order?.currency, order?.currencySymbol)}</p>
                </div>
              </div>
              <hr className="border-1 w-56" style={{ borderColor: 'gray' }} />
              <div className="flex  text-left">
                <p className="w-44">Total Price:</p>
                <p>{formatOrderCurrency(parseFloat(order?.price) - +(order?.couponDiscount || 0), order?.currency, order?.currencySymbol)}</p>
              </div>
            </div>
          )}

          {order?.orderType === OrderTypeEnum.BOOK_PURCHASE && (
            <div className="flex flex-col">
              <div className="font-bold text-sm">
                <div className="flex">
                  <div className="flex flex-col text-left gap-2 w-[]">
                    <p>{order?.extraInfo?.bookName}</p>
                    <p>Additional Pages:</p>
                    <p>Discount:</p>
                    <p>Price after Discount:</p>
                    <p>Quantity:</p>
                  </div>
                  <div className="flex flex-col text-left gap-2 w-44">
                    <p>{formatOrderCurrency(bookPrice, order?.currency, order?.currencySymbol)}</p>
                    <p>{formatOrderCurrency(order?.additionalPageCost || 0, order?.currency, order?.currencySymbol)}</p>
                    <p>{formatOrderCurrency(order?.couponDiscount || 0, order?.currency, order?.currencySymbol)}</p>
                    <p>{formatOrderCurrency(bookPurchasePriceAfterDiscount(order), order?.currency, order?.currencySymbol)}</p>

                    <p> {order?.quantity}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {order?.orderType === OrderTypeEnum.BOOK_PURCHASE && (
          <div className="flex flex-col gap-4">
            <h1 className="text-center font-bold">Order Total</h1>
            <div className="font-bold flex flex-col justify-evenly items-center gap-4 text-sm">
              <div className="flex">
                <div className="flex flex-col text-left gap-2 w-[9rem] md:w-72">
                  <p>Subtotal Price:</p>
                  <p>Shipping Price:</p>
                </div>
                <div className="flex flex-col text-left gap-2 w-44">
                  <p>{formatOrderCurrency(parseFloat(subTotal as string) < 1 ? 0 : parseFloat(subTotal as string), order?.currency, order?.currencySymbol)}</p>
                  <p>{formatOrderCurrency(order?.shippingAmount || 0, order?.currency, order?.currencySymbol)}</p>
                </div>
              </div>
              <hr className="w-[80%] md:w-[40%] border-1 mx-16" style={{ borderColor: 'gray' }} />
              <div className="flex">
                <div className="flex flex-col text-left gap-2 w-[8rem] md:w-72">
                  <p>Total Price:</p>
                </div>
                <div className="flex flex-col text-left gap-2 w-44">
                  <p>{formatOrderCurrency(calculateBookPurchaseTotalAmount(order), order?.currency, order?.currencySymbol)}</p>
                </div>
              </div>
            </div>

            <h1 className="text-center font-bold">Billing and Shipping</h1>
            <div className="font-bold flex flex-col justify-evenly items-center gap-4 text-sm">
              <div className="flex">
                <div className="flex flex-col text-left gap-2 w-[8rem] md:w-72">
                  <p>Billing From</p>
                  <p>{order?.billingCardHolderName}</p>
                  <p>
                    {order?.billingAddress1} {order?.billingAddress2}
                  </p>
                  <p>
                    {order?.billingCity},{order?.billingState} {order?.billingPostalCode}
                  </p>
                  <p>US</p>
                  <br />
                </div>
                <div className="flex flex-col text-left gap-2 w-44">
                  <p>Ship To</p>
                  <p>
                    {' '}
                    <p>
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p>
                      {order?.shippingAddress1} {order?.shippingAddress2}
                    </p>
                    <p>
                      {order?.shippingCity},{order?.shippingState} {order?.shippingZip}
                    </p>
                    <p>US</p>
                    <br />
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        <p className="text-center text-sm font-bold">
          Thank you for your business and we look forward to serving you in the future!
        </p>
      </div>
    </div>
  );
};
export default PurchaseConfirmation;
